'use client';

import {
  REDUCED_MOTION_QUERY,
  syncSkipAnimationsWithReducedMotion,
} from '@/utils/reducedMotion';
import { LazyMotion, MotionGlobalConfig } from 'framer-motion';
import type { ReactNode } from 'react';

// Module scope, not an effect: it must hold before the first client render
// starts an animation, and child effects run before this provider's would.
if (typeof window !== 'undefined') {
  syncSkipAnimationsWithReducedMotion(
    MotionGlobalConfig,
    window.matchMedia(REDUCED_MOTION_QUERY)
  );
}

/**
 * Lazy-load framer-motion's feature set so the initial bundle only ships
 * the ~4.6 KB `m` component shell, and the ~18 KB `domAnimation` feature
 * pack (animations + variants + AnimatePresence + hover/focus/press
 * gestures) loads asynchronously after first paint.
 *
 * Why: the chat-page graph imports ~11 components that use framer-motion
 * (filter-popup + its 10 sub-components + UsageLoader). With plain
 * `motion.div`, webpack bundled the full ~34 KB framer library into the
 * chat-route critical path (chunk 781 in bundle-analyzer). Switching those
 * sites to `m.div` and providing features via this LazyMotion provider
 * defers the 18 KB to a separate async chunk that loads after the route
 * is interactive.
 *
 * Feature pack choice: `domAnimation` (not `domMax`). The chat-page graph
 * uses animate / exit / variants / AnimatePresence / hover — all in
 * domAnimation. domMax adds pan/drag/layout animations which are only
 * needed on routes outside the chat-page graph; if a route inside this
 * wrapper actually needs them later, switch the import here to domMax.
 *
 * strict={true} is now ON: every framer-motion user in the [locale]
 * subtree has been converted from `motion.X` to `m.X` (PR introducing
 * this provider — full app-wide migration). LazyMotion will throw at
 * render time if a `motion.X` slips back in via a future PR, surfacing
 * the regression immediately instead of silently bloating the bundle.
 */
const loadDomAnimation = () =>
  import('framer-motion').then((mod) => mod.domAnimation);

export default function MotionProvider({ children }: { children: ReactNode }) {
  return (
    <LazyMotion features={loadDomAnimation} strict>
      {children}
    </LazyMotion>
  );
}
